import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface WorkflowStage {
  id: string;
  stage_key: string;
  stage_name: string;
  stage_order: number;
  color: string;
  job_status: string | null;
}

export const useTradeWorkflows = (propertyType: string, tradeCode: string) => {
  const { orgId } = useAuth();
  const [stages, setStages] = useState<WorkflowStage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadWorkflowStages = async () => {
      if (!orgId || !propertyType || !tradeCode) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Normalize workflow type
        const normalizedType = propertyType.toLowerCase();
        
        // Try to find trade-specific workflow first
        let { data: workflow, error: workflowError } = await supabase
          .from('workflows')
          .select('id')
          .eq('org_id', orgId)
          .eq('workflow_type', normalizedType)
          .eq('trade_code', tradeCode)
          .eq('is_active', true)
          .maybeSingle();

        // If no trade-specific workflow, fall back to generic workflow
        if (!workflow) {
          ({ data: workflow, error: workflowError } = await supabase
            .from('workflows')
            .select('id')
            .eq('org_id', orgId)
            .eq('workflow_type', normalizedType)
            .is('trade_code', null)
            .eq('is_active', true)
            .maybeSingle());
        }

        if (workflowError) {
          console.error('Error loading workflow:', workflowError);
          setStages(getDefaultStagesForTrade(tradeCode, normalizedType));
          setLoading(false);
          return;
        }

        if (!workflow) {
          console.log(`No workflow found for ${normalizedType}/${tradeCode}, using defaults`);
          setStages(getDefaultStagesForTrade(tradeCode, normalizedType));
          setLoading(false);
          return;
        }

        // Load stages for the workflow
        const { data: stagesData, error: stagesError } = await supabase
          .from('workflow_stages')
          .select('*')
          .eq('workflow_id', workflow.id)
          .order('stage_order');

        if (stagesError) {
          console.error('Error loading stages:', stagesError);
          setStages(getDefaultStagesForTrade(tradeCode, normalizedType));
        } else {
          setStages(stagesData || []);
        }
      } catch (error) {
        console.error('Error in loadWorkflowStages:', error);
        setStages(getDefaultStagesForTrade(tradeCode, 'retail'));
      } finally {
        setLoading(false);
      }
    };

    loadWorkflowStages();
  }, [orgId, propertyType, tradeCode]);

  return { stages, loading };
};

// Default stages for different trade types
const getDefaultStagesForTrade = (tradeCode: string, workflowType: string): WorkflowStage[] => {
  const trade = tradeCode.toLowerCase();
  
  // Roofing has the most comprehensive workflow
  if (trade === 'roofing') {
    return [
      { id: 'new', stage_key: 'new', stage_name: 'New Lead', stage_order: 1, color: '#3b82f6', job_status: 'new' },
      { id: 'inspection', stage_key: 'inspection', stage_name: 'Inspection', stage_order: 2, color: '#8b5cf6', job_status: 'inspection' },
      { id: 'estimate', stage_key: 'estimate', stage_name: 'Estimate', stage_order: 3, color: '#ec4899', job_status: 'estimate' },
      { id: 'contract', stage_key: 'contract', stage_name: 'Contract', stage_order: 4, color: '#f59e0b', job_status: 'contract' },
      { id: 'materials', stage_key: 'materials', stage_name: 'Materials', stage_order: 5, color: '#10b981', job_status: 'materials' },
      { id: 'install', stage_key: 'install', stage_name: 'Install', stage_order: 6, color: '#06b6d4', job_status: 'install' },
      { id: 'complete', stage_key: 'complete', stage_name: 'Complete', stage_order: 7, color: '#22c55e', job_status: 'complete' },
    ];
  }
  
  // Gutters - shorter workflow
  if (trade === 'gutters') {
    return [
      { id: 'new', stage_key: 'new', stage_name: 'New Lead', stage_order: 1, color: '#3b82f6', job_status: 'new' },
      { id: 'measure', stage_key: 'measure', stage_name: 'Measure', stage_order: 2, color: '#8b5cf6', job_status: 'measure' },
      { id: 'install', stage_key: 'install', stage_name: 'Install', stage_order: 3, color: '#06b6d4', job_status: 'install' },
      { id: 'complete', stage_key: 'complete', stage_name: 'Complete', stage_order: 4, color: '#22c55e', job_status: 'complete' },
    ];
  }
  
  // Siding workflow
  if (trade === 'siding') {
    return [
      { id: 'new', stage_key: 'new', stage_name: 'New Lead', stage_order: 1, color: '#3b82f6', job_status: 'new' },
      { id: 'inspection', stage_key: 'inspection', stage_name: 'Inspection', stage_order: 2, color: '#8b5cf6', job_status: 'inspection' },
      { id: 'estimate', stage_key: 'estimate', stage_name: 'Estimate', stage_order: 3, color: '#ec4899', job_status: 'estimate' },
      { id: 'contract', stage_key: 'contract', stage_name: 'Contract', stage_order: 4, color: '#f59e0b', job_status: 'contract' },
      { id: 'install', stage_key: 'install', stage_name: 'Install', stage_order: 5, color: '#06b6d4', job_status: 'install' },
      { id: 'complete', stage_key: 'complete', stage_name: 'Complete', stage_order: 6, color: '#22c55e', job_status: 'complete' },
    ];
  }
  
  // Windows workflow
  if (trade === 'windows') {
    return [
      { id: 'new', stage_key: 'new', stage_name: 'New Lead', stage_order: 1, color: '#3b82f6', job_status: 'new' },
      { id: 'measure', stage_key: 'measure', stage_name: 'Measure', stage_order: 2, color: '#8b5cf6', job_status: 'measure' },
      { id: 'order', stage_key: 'order', stage_name: 'Order', stage_order: 3, color: '#f59e0b', job_status: 'order' },
      { id: 'install', stage_key: 'install', stage_name: 'Install', stage_order: 4, color: '#06b6d4', job_status: 'install' },
      { id: 'complete', stage_key: 'complete', stage_name: 'Complete', stage_order: 5, color: '#22c55e', job_status: 'complete' },
    ];
  }
  
  // Default generic workflow for other trades
  return [
    { id: 'new', stage_key: 'new', stage_name: 'New', stage_order: 1, color: '#3b82f6', job_status: 'new' },
    { id: 'in_progress', stage_key: 'in_progress', stage_name: 'In Progress', stage_order: 2, color: '#f59e0b', job_status: 'in_progress' },
    { id: 'complete', stage_key: 'complete', stage_name: 'Complete', stage_order: 3, color: '#22c55e', job_status: 'complete' },
  ];
};
